<template>
  <div class="content container-fluid mt-7">
    <div class="card p-4 mb-4">
      <h3 class="mb-3">Upload CSV File</h3>
      <el-upload drag action="" :auto-upload="false" :file-list="fileList" :on-change="handleFileSelect"
        :before-upload="beforeUploadCheck" accept=".csv"
        class="w-100 p-sm-5 text-center border-2 border-dashed rounded">
        <template #default>
          <div class="w-100 d-flex flex-column align-items-center">
            <i class="el-icon-upload fs-1 text-secondary"></i>
            <div class="mt-2 w-100">
              Drop CSV here or <em class="text-primary">click to upload</em>
            </div>
            <div class="small text-muted">Only CSV files are accepted</div>
          </div>
        </template>
      </el-upload>
      <div class="d-flex justify-content-end mt-3">
        <el-button @click="redirectToUsers">Cancel</el-button>
        <el-button type="primary" :disabled="!csvFile" @click="compareCSV">Proceed</el-button>
      </div>
      <div v-if="comparisonDone" class="table-responsive mt-5">
        <h4 class="mb-3">Comparison Results</h4>
        <div class="d-flex justify-content-end mb-3">
          <el-input v-model="searchQuery" placeholder="Search by User ID or First Name" class="w-auto me-2" size="small"
            clearable />
          <el-button type="primary" size="small" @click="applySearch">Search</el-button>
        </div>
        <div class="d-none d-md-block">
          <el-table :data="paginatedData" border class="w-100 custom-csv-table">
            <el-table-column label="User ID" prop="user_id" :min-width="150" fixed>
              <template #default="scope">
                <span v-if="isChangedField(scope.row.user_id)" v-html="formatField(scope.row.user_id)"></span>
                <span v-else>{{ scope.row.user_id }}</span>
              </template>
            </el-table-column>
            <el-table-column label="First Name" prop="first_name" :min-width="150" fixed>
              <template #default="scope">
                <span v-if="isChangedField(scope.row.first_name)" v-html="formatField(scope.row.first_name)"></span>
                <span v-else>{{ scope.row.first_name }}</span>
              </template>
            </el-table-column>
            <template v-for="col in dynamicFields" :key="col">
              <el-table-column :label="col" :prop="col" :min-width="getColumnWidth(col)">
                <template #default="scope">
                  <span v-if="isChangedField(scope.row[col])" v-html="formatField(scope.row[col])"></span>
                  <span v-else>{{ scope.row[col] }}</span>
                </template>
              </el-table-column>
            </template>
          </el-table>
        </div>
        <div class="d-md-none">
          <el-table :data="paginatedData" border class="w-100 custom-csv-table">
            <el-table-column label="User ID" prop="user_id" :min-width="150">
              <template #default="scope">
                <span v-if="isChangedField(scope.row.user_id)" v-html="formatField(scope.row.user_id)"></span>
                <span v-else>{{ scope.row.user_id }}</span>
              </template>
            </el-table-column>
            <el-table-column label="First Name" prop="first_name" :min-width="150">
              <template #default="scope">
                <span v-if="isChangedField(scope.row.first_name)" v-html="formatField(scope.row.first_name)"></span>
                <span v-else>{{ scope.row.first_name }}</span>
              </template>
            </el-table-column>
            <template v-for="col in dynamicFields" :key="col">
              <el-table-column :label="col" :prop="col" :min-width="getColumnWidth(col)">
                <template #default="scope">
                  <span v-if="isChangedField(scope.row[col])" v-html="formatField(scope.row[col])"></span>
                  <span v-else>{{ scope.row[col] }}</span>
                </template>
              </el-table-column>
            </template>
          </el-table>
        </div>
        <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap">
          <div class="text-muted small ms-1 mb-2 mb-md-0">
            {{ tableInfoText }}
          </div>
          <div class="d-flex justify-content-end">
            <el-pagination background layout="prev, pager, next" :page-size="perPage" :current-page="currentPage"
              :total="filteredData().length" @current-change="handlePageChange" />
          </div>
        </div>
        <div class="d-flex justify-content-end mt-4">
          <el-button type="success" @click="onboardUsers">Onboard Users</el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Papa from "papaparse";
import Swal from 'sweetalert2';
import {
  ElUpload,
  ElButton,
  ElTable,
  ElTableColumn,
  ElInput,
  ElPagination,
  ElMessage
} from "element-plus";

export default {
  components: {
    ElUpload,
    ElButton,
    ElTable,
    ElTableColumn,
    ElInput,
    ElPagination,
    ElMessage
  },
  data() {
    return {
      csvFile: null,
      fileList: [],
      originalCSV: "",
      parsedOriginal: [],
      parsedUploaded: [],
      comparisonResult: [],
      comparisonDone: false,
      dynamicFields: [],
      currentPage: 1,
      perPage: 10,
      searchQuery: '',
      searchAppliedQuery: '',
    };
  },
  computed: {
    paginatedData() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.filteredData().slice(start, start + this.perPage);
    },
    tableInfoText() {
      const total = this.filteredData().length;
      const start = total === 0 ? 0 : (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, total);
      return `Showing ${start} to ${end} of ${total} entries`;
    }
  },
  methods: {
    beforeUploadCheck(file) {
      const isCSV = file.name.endsWith('.csv');
      if (!isCSV) {
        ElMessage.error('Only CSV files are allowed.');
      }
      return isCSV;
    },
    applySearch() {
      this.searchAppliedQuery = this.searchQuery;
      this.currentPage = 1;
    },
    redirectToUsers() {
      this.$router.push({ name: "Users" });
    },
    handleFileSelect(file, fileList) {
      this.csvFile = file.raw;
      this.fileList = fileList;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    getColumnWidth(field) {
      const customWidths = {
        nick_name: 140,
        feeder: 150,
        status: 150,
        pin: 160,
        api_secret: 200,
        totp_secret: 200,
        broker_id: 150,
        portfolio_size: 170,
        common_url: 200,
        port: 100,
        limit_percentage: 190,
        is_limit_order: 170,
        version: 190,
        portfolio_status: 190,
        profit_percentage: 200,
        loss_percentage: 200,
        created_date: 210,
        last_updated_date: 210,
        ...Object.fromEntries(Array.from({ length: 26 }, (_, i) => [`type${i + 1}`, 182])),
        ...Object.fromEntries(Array.from({ length: 26 }, (_, i) => [`multiplier${i + 1}`, 160])),
      };
      return customWidths[field] || 150;
    },
    compareCSV() {
      if (!this.csvFile.name.endsWith('.csv')) {
        ElMessage.error('Uploaded file is not a CSV.Please upload a valid CSV file.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const uploadedText = e.target.result;
        this.parsedUploaded = Papa.parse(uploadedText, {
          header: true,
          transformHeader: header => header.trim()
        }).data
          .filter(row => row && row.user_id)
          .map(row => ({
            ...row,
            portfolio_size: row.portfolio_size?.replace(/,/g, '') ?? '',
            pin: row.pin?.toString().replace(/^="?(.+?)"?$/, '$1') ?? ''
          }));
        const sessionCSV = sessionStorage.getItem("exportedCSV");
        if (sessionCSV) {
          this.originalCSV = sessionCSV;
          this.parsedOriginal = Papa.parse(sessionCSV, {
            header: true,
            transformHeader: header => header.trim()
          }).data
            .filter(row => row && row.user_id)
            .map(row => ({
              ...row,
              portfolio_size: row.portfolio_size?.replace(/,/g, '') ?? ''
            }));
        } else {
          console.log("No exportedCSV found in sessionStorage.");
        }
        this.compareData(this.parsedOriginal, this.parsedUploaded);
      };
      reader.readAsText(this.csvFile);
    },
    compareData(original, uploaded) {
      const keyField = "user_id";
      const typeMultiplierFields = [];
      for (let i = 1; i <= 26; i++) {
        typeMultiplierFields.push(`type${i}`, `multiplier${i}`);
      }
      const commonFields = [
        "user_id", "first_name", "last_name", "nick_name", "secret", "pin",
        "api_key", "api_secret", "totp_secret", "feeder", "status", "broker_id",
        "portfolio_status", "portfolio_size", "common_url", "port", "limit_percentage", "is_limit_order", "version", "profit_percentage", "loss_percentage"
      ];
      const dateFields = ["created_date", "last_updated_date"];
      const fieldList = [...commonFields, ...typeMultiplierFields, ...dateFields];
      this.dynamicFields = fieldList.filter(f => f !== "user_id" && f !== "first_name");
      const normalize = (val) => {
        if (val === null || val === undefined) return '';
        let str = val.toString().trim().replace(/^"+|"+$/g, '');
        if (str.includes(',')) {
          return str
            .split(',')
            .map(s => s.trim().replace(/^[-]+$/, '').replace(/\s+/g, ''))
            .filter(Boolean)
            .join(',');
        }
        const num = Number(str.replace(/\s+/g, ''));
        if (!isNaN(num)) return num.toString();
        return str.toLowerCase().replace(/\s+/g, '');
      };
      const originalMap = Object.fromEntries(original.map(row => [row[keyField], row]));
      const uploadedIds = new Set(uploaded.map(row => row[keyField]));
      const changed = [];
      const deleted = [];
      const unchanged = [];
      for (const newRow of uploaded) {
        const userId = newRow[keyField];
        const oldRow = originalMap[userId];
        const outputRow = {};
        const changes = [];
        if (oldRow) {
          const originalPairMap = {};
          for (let i = 1; i <= 26; i++) {
            const tKey = `type${i}`;
            const mKey = `multiplier${i}`;
            const tVal = normalize(oldRow[tKey] || '');
            const mVal = normalize(oldRow[mKey] || '');
            if (tVal) {
              const pair = `${tVal}__${mVal}`;
              originalPairMap[pair] = { tKey, mKey, rawT: oldRow[tKey], rawM: oldRow[mKey] };
            }
          }
          const uploadedPairMap = {};
          for (let i = 1; i <= 26; i++) {
            const tKey = `type${i}`;
            const mKey = `multiplier${i}`;
            const tVal = normalize(newRow[tKey] || '');
            const mVal = normalize(newRow[mKey] || '');
            if (tVal) {
              const pair = `${tVal}__${mVal}`;
              uploadedPairMap[pair] = { tKey, mKey, rawT: newRow[tKey], rawM: newRow[mKey] ?? '' };
            }
          }
          const usedSlots = new Set();
          Object.entries(originalPairMap).forEach(([pair, { tKey, mKey }]) => {
            const uploaded = uploadedPairMap[pair];
            if (uploaded) {
              outputRow[tKey] = uploaded.rawT;
              outputRow[mKey] = uploaded.rawM;
              usedSlots.add(tKey);
              usedSlots.add(mKey);
            } else {
              outputRow[tKey] = { old: originalPairMap[pair].rawT };
              outputRow[mKey] = { old: originalPairMap[pair].rawM };
              changes.push(tKey, mKey);
            }
          });
          let nextSlot = 1;
          Object.entries(uploadedPairMap).forEach(([pair, { rawT, rawM }]) => {
            if (originalPairMap[pair]) return;
            while (usedSlots.has(`type${nextSlot}`) || usedSlots.has(`multiplier${nextSlot}`)) {
              nextSlot++;
            }
            const tKey = `type${nextSlot}`;
            const mKey = `multiplier${nextSlot}`;
            outputRow[tKey] = rawT ? { new: rawT } : '';
            outputRow[mKey] = rawM ? { new: rawM } : '';
            usedSlots.add(tKey);
            usedSlots.add(mKey);
            if (rawT) changes.push(tKey);
            if (rawM) changes.push(mKey);
          });
          [...commonFields, ...dateFields].forEach(field => {
            const newValRaw = newRow[field] || '';
            const oldValRaw = oldRow[field] || '';
            const newVal = normalize(newValRaw);
            const oldVal = normalize(oldValRaw);
            if (newVal !== oldVal) {
              outputRow[field] = { new: newValRaw, old: oldValRaw };
              if (!['created_date', 'last_updated_date'].includes(field)) {
                changes.push(field);
              }
            } else {
              outputRow[field] = newValRaw || oldValRaw || '';
            }
          });
        } else {
          fieldList.forEach(f => {
            const val = newRow[f];
            if (val) {
              outputRow[f] = { new: val };
              changes.push(f);
            }
          });
        }
        outputRow._changes = changes;
        if (changes.length > 0) changed.push(outputRow);
        else unchanged.push(outputRow);
      }
      for (const oldRow of original) {
        const userId = oldRow[keyField];
        if (!uploadedIds.has(userId)) {
          const deletedRow = {};
          const changes = [];
          fieldList.forEach(f => {
            const val = oldRow[f];
            if (val) {
              deletedRow[f] = { old: val };
              changes.push(f);
            }
          });
          deletedRow._changes = changes;
          deleted.push(deletedRow);
        }
      }
      this.comparisonResult = [...changed, ...deleted, ...unchanged].sort((a, b) => {
        const aChanges = a._changes?.length || 0;
        const bChanges = b._changes?.length || 0;
        return bChanges - aChanges;
      });
      this.currentPage = 1;
      this.comparisonDone = true;
    },
    isChangedField(val) {
      return val && typeof val === 'object' && (val.new || val.old);
    },
    formatField(val) {
      const formatValue = (v) => {
        if (!v) return '';
        if (typeof v === 'string' && v.startsWith(',')) {
          return '-' + v;
        }
        return v;
      };
      if (!val) return '';
      if (val.new && val.old)
        return `<span class="bg-success-chat fw-bold">${formatValue(val.new)}</span><br><span><del>${formatValue(val.old)}</del></span>`;
      if (val.new)
        return `<span class="bg-success-chat fw-bold">${formatValue(val.new)}</span>`;
      if (val.old)
        return `<span class="text-danger"><del>${formatValue(val.old)}</del></span>`;
      return '';
    },
    onboardUsers() {
      const payload = this.parsedUploaded.map((user) => {
        const getVal = (val) => (val && typeof val === "object" ? val.new || val.old : val);
        let userProperties = { squareoffEnabled: '', portfolioProfitEnabled: '' };
        try {
          userProperties = JSON.parse(getVal(user.portfolio_status) || '{}');
        } catch (e) {
          console.warn('Invalid JSON in portfolio_status:', user.portfolio_status);
        }
        const typeObj = {};
        const multiplierObj = {};
        for (let i = 1; i <= 26; i++) {
          const typeKey = `type${i}`;
          const multiplierKey = `multiplier${i}`;
          const typeVal = getVal(user[typeKey]);
          const multVal = getVal(user[multiplierKey]);
          if (typeVal) typeObj[typeKey] = typeVal;
          if (multVal) multiplierObj[multiplierKey] = multVal;
        }
        const rawIsLimit = getVal(user.is_limit_order);
        const isLimit =
          rawIsLimit && rawIsLimit.toString().trim() !== ''
            ? rawIsLimit.toString().trim().toLowerCase() === 'true'
            : undefined;
        return {
          userId: getVal(user.user_id),
          firstName: getVal(user.first_name),
          lastName: getVal(user.last_name),
          nickName: getVal(user.nick_name),
          secret: getVal(user.secret),
          pin: getVal(user.pin)?.toString(),
          apiKey: getVal(user.api_key),
          apiSecret: getVal(user.api_secret),
          feeder: getVal(user.feeder)?.toLowerCase() === 'true',
          status: getVal(user.status),
          broker: getVal(user.broker_id),
          totpSecret: getVal(user.totp_secret),
          portfolioSize: Math.round(parseFloat(getVal(user.portfolio_size) || 0)),
          commonUrl: getVal(user.common_url),
          port: getVal(user.port),
          limitPercentage: getVal(user.limit_percentage),
          isLimitOrder: isLimit,
          version: getVal(user.version),
          profitPercentage: getVal(user.profit_percentage),
          lossPercentage: getVal(user.loss_percentage),
          userProperties,
          type: typeObj,
          multiplier: multiplierObj
        };
      });
      this.$store.dispatch("userStore/onBoardNewUsers", payload, { root: true })
        .then(() => {
          Swal.fire({
            title: 'Users successfully onboarded via CSV upload.',
            icon: 'success',
            confirmButtonText: 'Back to Users',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-secondary'
            }
          }).then(() => {
            this.redirectToUsers();
          });
        })
        .catch(error => {
          console.error("Failed to onboard user:", error);
          Swal.fire({
            title: 'Error',
            text: 'Something went wrong while onboarding the user.',
            icon: 'error',
            confirmButtonText: 'Close',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-danger'
            }
          });
        });
    },
    filteredData() {
      const normalize = (val) =>
        (val || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[\s_]+/g, '');
      const search = normalize(this.searchAppliedQuery);
      return this.comparisonResult.filter(row => {
        const getValue = (val) => {
          if (typeof val === 'object') return normalize(val.new || val.old);
          return normalize(val);
        };
        const exactNumericMatch = () => {
          const parsed = Number(this.searchAppliedQuery);
          if (isNaN(parsed)) return false;
          const portfolioSize = Number(getValue(row.portfolio_size));
          return parsed === portfolioSize;
        };
        const matchesTextFields = ['user_id', 'first_name', 'status'].some(field =>
          getValue(row[field]).includes(search)
        );
        const matchesTypeFields = Array.from({ length: 26 }, (_, i) => `type${i + 1}`).some(typeKey =>
          getValue(row[typeKey]).includes(search)
        );
        return exactNumericMatch() || matchesTextFields || matchesTypeFields;
      });
    },
  }
}
</script>
